import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import FCheckBox from 'components/BaseComponents/FCheckBox'
import FImage from 'components/BaseComponents/FImage'
import SBox from 'components/opinionless/SBox'
import SInputError from 'components/opinionless/SInputErrorV2'
import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'
import { LBButton, LBText } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { HOUSEHOLD_MEMBER_RELATIONSHIP } from 'containers/Enrollment/HouseholdIncome/HouseholdIncomeMembersDOB'
import { useTranslation } from 'i18n'
import React, { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { StepWizardChildProps } from 'react-step-wizard'
import { SStyled, useSTheme } from 'styles/theme'
import { useBenefitsAnswersMutation } from 'types/graphql'
import { getAge } from 'utils/date'
import { ga4_useTitle } from 'utils/ga4'
import XBanner from 'v2/components/XBanner'
import { ApplicationContext } from 'v2/context/application'
import { useApplicationBasicInfo } from 'v2/hooks/useApplicationBasicInfo'
import { useHouseholdMembers } from 'v2/hooks/useHouseholdMembers'
import style from './style.module.scss'

interface Props {
    setCurrentStep?: (number) => void
    onSaveAndContinue: (string) => void
    previouslySelectedMembers: any
    applicationId: string
    personId: string
    fontFamily: any
    onNextStep?: any
    onPrevious?: any
    updatePercentage: any
}

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`

const HouseholdIncomeChoice: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)
    const applicationContext = useContext(ApplicationContext)

    const {
        loading: householdMembersLoading,
        error: householdMembersError,
        refetch: householdMembersRefetch,
    } = useHouseholdMembers()
    useApplicationBasicInfo()

    useEffect(() => {
        householdMembersRefetch()
    }, [householdMembersRefetch])

    const { handleSubmit, errors, setError } = useForm()

    const [saveBenefitAnswer, { loading: saveAnswersLoading }] = useBenefitsAnswersMutation()
    const { refetch } = useApplicationBasicInfo()

    const [householdMembersDetails, setHouseholdMembersDetails] = useState([])

    const [selectedMembers, setSelectedMembers] = useState(
        props.previouslySelectedMembers
            ? props.previouslySelectedMembers
            : {
                  membersWithIncome: [],
                  applicantSelected:
                      parseInt(applicationContext.profile.earnedIncome) > 0 ||
                      parseInt(applicationContext.profile.unearnedIncome) > 0,
              }
    )

    const [unselectedMembers, setUnselectedMembers] = useState([])

    const [loading, setLoading] = useState(true)

    ga4_useTitle('Household Income Choice')

    const fontFamily = props.fontFamily

    useEffect(() => {
        if (!householdMembersLoading) {
            setLoading(false)
            const allMemberIds = [
                ...applicationContext.householdMembers.map((member) => member.id),
                applicationContext.profile.person.id,
            ]

            setHouseholdMembersDetails(
                applicationContext.householdMembers.map((member) => ({
                    id: member.id,
                    name: member.name,
                    relation: member.relation,
                    dob: member.dob,
                    blindDisabled: member.blindDisabled,
                    earnedIncome: member.earnedIncome,
                    unearnedIncome: member.unearnedIncome,
                }))
            )

            let selectedMemberIds = selectedMembers.membersWithIncome.slice()
            selectedMemberIds = selectedMemberIds.concat(
                applicationContext.householdMembers
                    .filter((member) => parseInt(member.earnedIncome) > 0 || parseInt(member.unearnedIncome) > 0)
                    .map((member) => member.id)
            )
            if (selectedMembers.applicantSelected) {
                selectedMemberIds.push(applicationContext.profile.person.id)
            }

            const unselectedMemberIds = allMemberIds.filter((id) => !selectedMemberIds.includes(id))

            setUnselectedMembers(unselectedMemberIds)

            const membersWithIncome = applicationContext.householdMembers
                .filter((member) => parseInt(member.earnedIncome) > 0 || parseInt(member.unearnedIncome) > 0)
                .map((member) => member.id)

            setSelectedMembers((prev) => ({
                ...prev,
                membersWithIncome: [...prev.membersWithIncome, ...membersWithIncome],
            }))
        }
    }, [])

    const handleMemberToggle = (memberId) => {
        setSelectedMembers((prev) => {
            const isMemberSelected = prev.membersWithIncome.includes(memberId)

            if (isMemberSelected) {
                return {
                    ...prev,
                    membersWithIncome: prev.membersWithIncome.filter((id) => id !== memberId),
                }
            } else {
                return {
                    ...prev,
                    membersWithIncome: [...prev.membersWithIncome, memberId],
                }
            }
        })

        setUnselectedMembers((prev) => {
            return prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
        })
    }

    const handleApplicantToggle = () => {
        setSelectedMembers((prev) => {
            const updatedApplicantSelected = !prev.applicantSelected

            return {
                ...prev,
                applicantSelected: updatedApplicantSelected,
            }
        })

        setUnselectedMembers((prev) => {
            return selectedMembers.applicantSelected
                ? [...prev, applicationContext.profile.person.id]
                : prev.filter((id) => id !== applicationContext.profile.person.id)
        })
    }

    const unselectedMemberObjects = unselectedMembers.flatMap((unselectedMember) => {
        const earnedIncomeObject = {
            householdMemberId: unselectedMember === applicationContext.profile.person.id ? null : unselectedMember,
            personId: applicationContext.profile.person.id,
            category: 'EARNED_INCOME',
            answer: '0',
        }

        const unearnedIncomeObject = {
            householdMemberId: unselectedMember === applicationContext.profile.person.id ? null : unselectedMember,
            personId: applicationContext.profile.person.id,
            category: 'UNEARNED_INCOME',
            answer: '0',
        }

        return [earnedIncomeObject, unearnedIncomeObject]
    })

    const onSubmit = async () => {
        try {
            const response = await saveBenefitAnswer({
                variables: {
                    answers: unselectedMemberObjects,
                },
            })

            if (!response.data.addBenefitAnswers.ok) {
                setError('lbHouseholdIncomeChoice', { type: 'oops', message: t('error') })
            }
        } catch (error) {
            setError('lbHouseholdIncomeChoice', { type: 'oops', message: t('error') })
        }
        await refetch()

        props.onSaveAndContinue(selectedMembers)
        props.nextStep()
    }

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.householdInfo,
        WIZARD_STEPS_MAPPING.householdInfo.incomeChoice,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    if (loading) {
        return null
    }
    return (
        <Container>
            <SBox as="form" onSubmit={handleSubmit(onSubmit)}>
                <FButtonPrevious onClick={props.previousStep} />
                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: fontFamily.body,
                        fontWeight: '800',
                        lineHeight: '140%',
                        color: '#202020',
                        fontSize: '16px',
                        marginBottom: '24px',
                    }}
                >
                    {t('long_beach_household_income_choice_subtitle')}
                </LBText>
                <XBanner
                    title={t('long_beach_household_income_choice_banner_title')}
                    description={t('long_beach_household_income_choice_banner_description')}
                    isButton={false}
                    type="warning"
                />
                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: fontFamily.body,
                        fontWeight: '400',
                        lineHeight: '140%',
                        color: theme.colors.gray400,
                        fontSize: '14px',
                        marginBottom: '24px',
                    }}
                    dangerouslySetInnerHTML={{
                        __html: t('long_beach_household_income_choice_description'),
                    }}
                />
                <div className={style.household_applicant_income_choice_container}>
                    <div className={style.household_applicant_income_choice_container__icon}>
                        <FImage imageUrl={'/lb_household_income_person.svg'} alt="person" />
                    </div>
                    <div className={style.household_applicant_income_choice_container__applicant_group}>
                        <LBText
                            sx={{
                                textAlignLast: 'left',
                                whiteSpace: 'pre-line',
                                fontFamily: fontFamily.body,
                                fontWeight: '700',
                                lineHeight: '140%',
                                color: theme.colors.lbBlue,
                                fontSize: '14px',
                                marginBottom: '5px',
                            }}
                            dangerouslySetInnerHTML={{
                                __html: t('long_beach_applicant_name_label_relationship', {
                                    name: `${applicationContext?.profile?.firstName} ${applicationContext?.profile?.lastName}`,
                                    relationship: t('long_beach_household_applicant_label'),
                                }),
                            }}
                        />
                        <FCheckBox
                            name={'applicant_income_choice'}
                            id={'applicantIncomeChoice'}
                            value={'applicant_income'}
                            isChecked={
                                selectedMembers?.applicantSelected ||
                                parseInt(applicationContext.profile.earnedIncome) > 0 ||
                                parseInt(applicationContext.profile.unearnedIncome) > 0
                            }
                            text={t('long_beach_household_income_option')}
                            onChange={handleApplicantToggle}
                        />
                    </div>
                </div>
                {householdMembersDetails.map((member, index) => (
                    <div className={style.household_member_income_choice_container}>
                        <div className={style.household_member_income_choice_container__icon}>
                            <FImage imageUrl={'/lb_household_income_person_bw.svg'} alt="person" />
                        </div>
                        <div className={style.household_member_income_choice_container__member_group}>
                            <LBText
                                sx={{
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    fontFamily: fontFamily.body,
                                    fontWeight: '700',
                                    lineHeight: '140%',
                                    color: theme.colors.gray400,
                                    fontSize: '14px',
                                    marginBottom: '5px',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: t('long_beach_member_name_label_relationship_age', {
                                        name: `${member.name}`,
                                        relationship: t(`${HOUSEHOLD_MEMBER_RELATIONSHIP[member.relation]}`),
                                        age: `${getAge(member.dob)}`,
                                    }),
                                }}
                            />
                            <FCheckBox
                                name={'member_income_choice'}
                                id={'memberIncomeChoice'}
                                value={'member_income'}
                                isChecked={
                                    selectedMembers?.membersWithIncome.includes(member.id) ||
                                    parseInt(member.earnedIncome) > 0 ||
                                    parseInt(member.unearnedIncome) > 0
                                }
                                text={t('long_beach_household_income_option')}
                                onChange={() => {
                                    handleMemberToggle(member.id)
                                }}
                            />
                        </div>
                    </div>
                ))}
                <LBButton
                    type={'submit'}
                    id={'continueButton'}
                    title={t('continue')}
                    loading={saveAnswersLoading}
                    width={'100%'}
                    mt={7}
                    mb={5}
                    textStyle={{
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '140%',
                        fontFamily: fontFamily.body,
                        color: theme.colors.white,
                    }}
                >
                    {t('save_and_continue')}
                </LBButton>
                {errors.lbHouseholdIncomeChoice && (
                    <SInputError
                        sx={{
                            fontFamily: fontFamily.body,
                            color: theme.colors.redError,
                        }}
                        forElementId={'continueButton'}
                    >
                        {t(errors.lbHouseholdIncomeChoice?.message)}
                    </SInputError>
                )}
            </SBox>
        </Container>
    )
}

export default HouseholdIncomeChoice
